import React from 'react';
import { Button, Dropdown, Typography, message } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import type { IncidentsProjection } from '../../routes/execution/dashboardProjection';
import type { ExecutionIncidentType } from '../../routes/execution/incidentFacade';

const { Text } = Typography;

type Props = {
  incidents: IncidentsProjection;
  onResolve?: (incidentId: number) => void;
  onOpenIncidentReport?: (preset?: ExecutionIncidentType) => void;
  resolvingId?: number | null;
};

function formatTs(value: string): string {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
}

export const IncidentsCompact: React.FC<Props> = ({
  incidents,
  onResolve,
  onOpenIncidentReport,
  resolvingId = null
}) => {
  const active = incidents.items.filter((i) => i.isActive);
  if (active.length === 0) return null;

  return (
    <section className="execution-workspace__section">
      <div className="execution-workspace__section-title">Инциденты</div>
      {active.map((item) => (
        <div
          key={item.id}
          className={`execution-workspace__incident execution-workspace__incident--${item.severity}`}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 12 }}>
                {item.severity === 'high' ? '🔴' : item.severity === 'medium' ? '🟠' : '⚪'}{' '}
                {item.typeLabel}
              </Text>
              <div className="execution-workspace__muted">{formatTs(item.startedAt)}</div>
              {item.comment ? (
                <Text style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                  {item.comment}
                </Text>
              ) : null}
              <div className="execution-workspace__muted" style={{ fontSize: 10, marginTop: 4 }}>
                {item.affectsETA ? 'ETA · ' : ''}
                {incidents.hasActiveBlocking && item.severity === 'high' ? 'блокирует · ' : ''}
                {item.financialImpact ? 'финансы' : ''}
              </div>
            </div>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'resolve',
                    label: 'Закрыть',
                    onClick: () => onResolve && void onResolve(item.id)
                  },
                  {
                    key: 'comment',
                    label: 'Комментарий',
                    onClick: () => message.info('Комментарий к инциденту — в следующей фазе')
                  },
                  {
                    key: 'escalate',
                    label: 'Эскалация',
                    onClick: () => message.info('Эскалация зарегистрирована (workflow)')
                  },
                  {
                    key: 'downtime',
                    label: 'Простой',
                    onClick: () => onOpenIncidentReport?.('downtime')
                  },
                  {
                    key: 'note',
                    label: 'Заметка',
                    onClick: () => message.info('Заметка — в следующей фазе')
                  }
                ]
              }}
              trigger={['click']}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} loading={resolvingId === item.id} />
            </Dropdown>
          </div>
          {onResolve ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontSize: 11, marginTop: 4 }}
              loading={resolvingId === item.id}
              onClick={() => onResolve(item.id)}
            >
              Закрыть инцидент
            </Button>
          ) : null}
        </div>
      ))}
    </section>
  );
};

export default IncidentsCompact;
